import React from "react";
import ReactDOMServer from "react-dom/server";

import type { MutableProseNodeAttrs } from "..";
import { faChevronDown, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import fastdom from "fastdom";
import { throttle } from "lodash";
import { NodeRange, Node as ProseNode, Slice } from "prosemirror-model";
import { EditorState, NodeSelection, Plugin, PluginKey, TextSelection, Transaction } from "prosemirror-state";
import {
	Decoration,
	DecorationSet,
	EditorView,
	__serializeForClipboard as serializeForClipboard,
} from "prosemirror-view";
import { v4 as uuid4 } from "uuid";

import { BlockSelection } from "../blockselection";
import { adjustSelectionToIncludeDescendants, expandRangeToIncludeDescendants, removeBlockById } from "../commands";
import { Extension } from "../extension";
import { ImageStrip } from "../nodes/image-strip";
import { LIST_ITEM_TYPE_NAMES } from "../nodes/plaintext";
import { expandSelectionToBlocks } from "../utils";
import { buildTree } from "./blockBoundary";
import { collapsePluginEnabled, isCollapsed, isCollapsible, posIsInCollapsedSection, setCollapsed } from "./collapse";
import { getContext } from "./context";
import DropTarget, { DropTargetHandler } from "./dropTarget";
import MousePos, { type ViewDesc } from "./mousePos";
import { createPosBookmark, PosBookmark } from "./posbookmark";

import styles from "./multiselect.module.css";

/* We use the internal __serializeForClipboard method */
declare module "prosemirror-view" {
	// eslint-disable-next-line no-underscore-dangle
	export const __serializeForClipboard: (view: EditorView, slice: Slice) => { dom: HTMLDivElement; text: string };
}

class Mirror {
	options: {
		container: Element;
		classDragContainer: string;
	};
	wrapper: HTMLElement;

	constructor(options = {}) {
		this.options = {
			container: document.body,
			classDragContainer: "multiselect-drag",
			...options,
		};
		this.wrapper = document.createElement("div");
		this.wrapper.style.position = "absolute";
	}

	open(populate: (wrapper: HTMLElement) => void) {
		this.close();
		this.options.container.appendChild(this.wrapper);
		this.wrapper.classList.add(this.options.classDragContainer);
		populate(this.wrapper);
	}

	close() {
		while (this.wrapper.lastChild) this.wrapper.removeChild(this.wrapper.lastChild);
		this.wrapper.remove();
	}

	update({ x = 0, y = 0 } = {}) {
		this.wrapper.style.transform = `translate(${x}px, ${y}px)`;
	}

	children(): Element[] {
		return Array.from(this.wrapper.children);
	}
}

const MultiselectMap = new Map<string, MultiSelect>();

let mouseState: "up" | "down" = "up";
let rightClickFlag = false;

function isCustomEvent(event: Event): event is CustomEvent {
	return "detail" in event;
}

// eslint-disable-next-line max-len
const GRABBER_HTML = `<svg width="16" height="10" viewBox="0 0 6 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 1C6 1.55228 5.55228 2 5 2C4.44772 2 4 1.55228 4 1C4 0.447715 4.44772 0 5 0C5.55228 0 6 0.447715 6 1ZM5 6C5.55228 6 6 5.55228 6 5C6 4.44772 5.55228 4 5 4C4.44772 4 4 4.44772 4 5C4 5.55228 4.44772 6 5 6ZM5 10C5.55228 10 6 9.55229 6 9C6 8.44771 5.55228 8 5 8C4.44772 8 4 8.44771 4 9C4 9.55229 4.44772 10 5 10ZM1 10C1.55228 10 2 9.55229 2 9C2 8.44771 1.55228 8 1 8C0.447715 8 0 8.44771 0 9C0 9.55229 0.447715 10 1 10ZM2 5C2 5.55228 1.55228 6 1 6C0.447715 6 0 5.55228 0 5C0 4.44772 0.447715 4 1 4C1.55228 4 2 4.44772 2 5ZM1 2C1.55228 2 2 1.55228 2 1C2 0.447715 1.55228 0 1 0C0.447715 0 0 0.447715 0 1C0 1.55228 0.447715 2 1 2Z"/></svg>`;

const chevronStyle = {
	fontSize: "0.75rem",
	width: "16px",
	position: "relative",
} as const;
const RIGHT_CHEVRON_HTML = ReactDOMServer.renderToStaticMarkup(
	React.createElement(FontAwesomeIcon, { icon: faChevronRight, style: chevronStyle }),
);
const DOWN_CHEVRON_HTML = ReactDOMServer.renderToStaticMarkup(
	React.createElement(FontAwesomeIcon, { icon: faChevronDown, style: chevronStyle }),
);

const multiSelectPluginKey = new PluginKey<{ dragging: boolean }>("multiselect");

export function currentlyDragging(state: EditorState): boolean {
	return multiSelectPluginKey.getState(state)?.dragging ?? false;
}

export default class MultiSelect extends Extension {
	// A container of the blocks being dragged.
	mirror: Mirror;
	// The visual indicator of where a drop will occur.
	// dropTargetIndicator: HTMLElement;
	dragHandle: HTMLDivElement;
	// The document position of the node beneath the mouse. -1 if no known
	// node.
	contextHint: PosBookmark | null;
	uuid: string;
	anchor: ViewDesc | null = null;
	forceHide = false;

	get name() {
		return "multiselect";
	}

	constructor(options = {}) {
		super(options);
		this.uuid = uuid4();
		this.contextHint = null;
		this.mirror = new Mirror({
			classDragContainer: this.options.selectionDragClass,
		});
		this.mirror.update({ x: -2000, y: -2000 });

		const dragHandle = (this.dragHandle = document.createElement("div"));
		dragHandle.classList.add(...this.options.selectionTabClass.split(" "));
		dragHandle.innerHTML = GRABBER_HTML;
		dragHandle.draggable = true;
		dragHandle.addEventListener("dragstart", this.onDragStart);
		dragHandle.addEventListener("dragend", this.onDragEnd);
		dragHandle.addEventListener("click", this.onDragHandleClick);

		this.updateHandlePosition = throttle(this.updateHandlePosition, 50);
	}

	init() {
		if (this.editor) {
			MultiselectMap.set(this.uuid, this);
		}
		if (this.editorView) {
			MousePos.subscribe(this.editorView, this.onAnchorChange);
		}
	}

	destroy() {
		if (this.editor) {
			MultiselectMap.delete(this.uuid);
		}
		if (this.editorView) {
			MousePos.unsubscribe(this.editorView, this.onAnchorChange);
		}
		if (this.mirror) {
			this.mirror.close();
		}
		this.dragHandle.removeEventListener("dragstart", this.onDragStart);
		this.dragHandle.removeEventListener("dragend", this.onDragEnd);
		this.contextHint?.discard();
	}

	get defaultOptions() {
		return {
			// CSS class used for a selected block in a multi-select group.
			selectionClass: "multiselect-block",
			// CSS class used for the drag-and-drop dragHandle.
			selectionTabClass: "multiselect-drag-handle",
			// CSS class used for the drag container
			selectionDragClass: "multiselect-drag",
		};
	}

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				key: multiSelectPluginKey,
				appendTransaction: extension.appendTransaction.bind(extension),
				state: {
					init: () => ({ dragging: false }),
					apply(tr, value, _oldState, _newState) {
						if (tr.getMeta(multiSelectPluginKey) != null) {
							return { dragging: tr.getMeta(multiSelectPluginKey).dragging };
						}
						return value;
					},
				},
				props: {
					decorations: extension.decorations,
					attributes: () => ({ class: styles.editor }),
					handleDOMEvents: {
						mousedown: (_, event) => {
							mouseState = "down";
							// Button 2 is the Right Click button
							if (event.button === 2) rightClickFlag = true;
							extension.forceHide = true;
							extension.hide();
							return false;
						},
						mouseup: () => {
							mouseState = "up";
							extension.forceHide = false;
							extension.updateHandlePosition(extension.anchor);
							return false;
						},
					},
				},
			}),
		];
	}

	decorations = (state: EditorState) => {
		const decorations: Decoration[] = [];
		const { selection } = state;
		if (!selection) return;

		// CX-1562: A user complained about right-clicks on empty blocks selecting the block below.
		// In Chrome and Safari, right clicking while not having an active selection selects the word.
		// In the case of whitespace, the contiguous segment of whitespace is selected.
		// If the right-click places the cursor at the end of a line, the selection goes from the end of that line to the
		// 	start of the next.
		// This so far is the browser's behaviour.
		// In Fellow, when calling the `blockRange` method above, since the selection *technically* is on two lines, the
		// 	selection is expanded to both of those lines, which is the behaviour we don't want.
		// The way I chose to fix is this is to not expand the selection at all when right-clicking if it ends on a new line.
		// Conceivably there are other ways to solve this, but I think this is the best UX. -Wal
		if (rightClickFlag) {
			rightClickFlag = false;
			return;
		}

		const range = selection.$from.blockRange(selection.$to);
		if (!range) return;
		// The selection spans multiple lines
		state.doc.nodesBetween(range.start, range.end, (node, pos) => {
			// If this isn't a block-level node we don't care about it.
			// Generally these are the TextNode fragments inside leafs..
			if (!node.attrs.blockId) return;
			// For every node intersected by the selection we add a highlight
			// class if this selection crosses multiple lines.
			// This assumes that blocks are always top level
			if (selection.$to.start(1) !== selection.$from.start(1)) {
				decorations.push(
					Decoration.node(pos, pos + node.nodeSize, {
						class: this.options.selectionClass,
					}),
				);
			}
			// We only care about top-level nodes in the document so we always
			// return False to stop descent into children.
			return false;
		});

		return DecorationSet.create(state.doc, decorations);
	};

	hide = () => {
		this.dragHandle.style.opacity = "0";
		this.dragHandle.style.pointerEvents = "none";
		this.contextHint?.discard();
		this.contextHint = null;
	};

	show = () => {
		this.dragHandle.style.opacity = "1";
		this.dragHandle.style.pointerEvents = "all";
	};

	onAnchorChange = (event: Event) => {
		if (isCustomEvent(event)) {
			this.anchor = event.detail;
			this.updateHandlePosition(event.detail);
		}
	};

	/**
	 * Update the location of the dragHandle, or remove it if pos is -1.
	 */
	updateHandlePosition(anchor: ViewDesc | null) {
		const { dragHandle, editorView: view } = this;

		fastdom.measure(() => {
			if (!anchor || !dragHandle || !view || !anchor.node) {
				fastdom.mutate(this.hide);
				return;
			}
			if (anchor.dirty) {
				return;
			}

			let showChevron: "right" | "down" | undefined;
			if (isCollapsible(view.state, anchor.node) && collapsePluginEnabled(view.state)) {
				showChevron = isCollapsed(view.state, anchor.node) ? "right" : "down";
			}

			const related = anchor.dom as HTMLElement;

			this.contextHint = createPosBookmark(view.state, anchor.posAtStart);

			const { top, height } = related.getBoundingClientRect();
			const parentElement = view.dom.parentElement;
			if (!parentElement) return;
			const parentRect = parentElement.getBoundingClientRect();
			const parentScrollTop = parentElement.scrollTop;
			const relatedStyle = window.getComputedStyle(related);
			const relatedPaddingTop = parseInt(relatedStyle.paddingTop.split("px")[0]);
			const dragHandleHasParent = !!dragHandle.parentNode;
			fastdom.mutate(() => {
				let topOffset = relatedPaddingTop;

				if (anchor.node?.type.name === "horizontal_rule") {
					topOffset = relatedPaddingTop - 12;
				} else if (relatedPaddingTop || anchor.node?.type.name === "header-one") {
					topOffset = relatedPaddingTop - 4;
				}

				dragHandle.style.transform = `translateY(${top + topOffset + parentScrollTop - parentRect.top}px)`;
				dragHandle.style.lineHeight = relatedStyle.lineHeight;
				if (height >= 29) {
					// hard coded positioning.
					dragHandle.style.paddingTop = "2px";
				} else {
					dragHandle.style.paddingTop = "";
				}
				if (showChevron === "right") {
					dragHandle.innerHTML = RIGHT_CHEVRON_HTML;
				} else if (showChevron === "down") {
					dragHandle.innerHTML = DOWN_CHEVRON_HTML;
				} else {
					dragHandle.innerHTML = GRABBER_HTML;
				}
				if (!this.forceHide) this.show();

				if (!dragHandleHasParent && view.dom.parentNode) {
					view.dom.parentNode.insertBefore(dragHandle, view.dom);
				}
			});
		});
	}

	onDragHandleClick = () => {
		const { editorView: view } = this;
		if (!view) return;
		if (!this.anchor) return;

		if (!this.anchor.node || !isCollapsible(view.state, this.anchor.node)) return;
		setCollapsed(view, this.anchor.node, !isCollapsed(view.state, this.anchor.node));
		// In Chrome but not Firefox, the cursor can end up in a hidden node after
		// collapsing a section even though we moved it to the end of the header text.
		// This is a workaround for that. Without it, clicking at the beginning of the next
		// header does not change the cursor position resulting in the bug shown in MEET-890.
		//
		// For the narrow-width collapse/expand button and the "Expand section"
		// button we just preventDefault on mousedown, which stops focus from
		// changing at all. Here though, doing so would prevent dragging so we need
		// to manually refocus the view after the click happens.
		view.focus();
	};

	onDragEnd = (_e: DragEvent) => {
		const { editorView: view } = this;
		if (!view) return;
		view.dragging = null;
		view.dispatch(view.state.tr.setMeta(multiSelectPluginKey, { dragging: false }));
	};

	onDragStart = (e: DragEvent) => {
		const { editorView: view } = this;
		if (!view) return;

		view.dispatch(view.state.tr.setMeta(multiSelectPluginKey, { dragging: true }));
		this._adjustSelectionBasedOnDragger();
		adjustSelectionToIncludeDescendants(view.state, view.dispatch);

		const range = view.state.selection.$from.blockRange(view.state.selection.$to);
		if (!range) return;
		let rangeStart = range.start;
		let rangeEnd = range.end;

		this.mirror.open(wrapper => {
			wrapper.classList.add("ProseMirror");
			wrapper.style.width = `${view.dom.clientWidth}px`;

			view.state.doc.nodesBetween(rangeStart, rangeEnd, (node, pos) => {
				// If this isn't a block-level node we don't care about it.
				// Generally these are the TextNode fragments inside leafs..
				if (!node.attrs.blockId) return;
				const dom = view.nodeDOM(pos);
				if (dom) {
					const copy = dom.cloneNode(true);
					if (copy) {
						// In Chrome, as soon as you add a new iframe to the DOM, this whole drag handler stops working
						if (node.type.name === "embed" && copy instanceof HTMLElement) {
							for (const iframe of copy.querySelectorAll("iframe")) {
								iframe.remove();
							}
						}
						if (pos < rangeStart) rangeStart = pos;
						if (pos > rangeEnd) rangeEnd = pos;
						wrapper.appendChild(copy);
					}
				}
				return false;
			});
		});

		if (e.dataTransfer) {
			// This affects if the selected blocks are copied or moved
			// within the same document. If the user releasing the shiftkey
			// while dragging has no effect on this initial value.
			const copying = e.shiftKey;

			e.dataTransfer.clearData();
			e.dataTransfer.setDragImage(this.mirror.wrapper, 0, 0);
			e.dataTransfer.dropEffect = copying ? "copy" : "move";
			e.dataTransfer.effectAllowed = "copyMove";

			const slice = view.state.doc.slice(rangeStart, rangeEnd);
			const { dom, text } = serializeForClipboard(view, slice);

			view.dragging = {
				slice,
				move: !copying,
			};

			e.dataTransfer.setData("text/html", dom.innerHTML);
			e.dataTransfer.setData("text/plain", text);
			// this type should always be set so we know this is an event we can handle
			e.dataTransfer.setData(
				"json/fellow/prose",
				JSON.stringify({ slice: slice.toJSON(), originalMultiselectUUID: this.uuid }),
			);
		}
	};

	_adjustSelectionBasedOnDragger = () => {
		// The section might be might be different from the block that is being dragged by the dragger.
		// If so, we want to set the selection to the section so that the section is moved.

		const { editorView: view } = this;
		if (!view) return;

		let range = view.state.selection.$from.blockRange(view.state.selection.$to);
		const contextHint = this.contextHint?.mapToNow();

		// The user has a different text selection then where they started
		// dragging from.
		if (contextHint != null && (!range || contextHint <= range.start || contextHint >= range.end)) {
			const tr = view.state.tr;
			const $contextHint = tr.doc.resolve(contextHint);

			// Check if the resolved contextHint is not a position inside a block
			// 	Should only happen when attempting to select an embed block (which is a leaf node)
			if ($contextHint.depth === 0) {
				// The node we're looking for should follow contextHint, since contextHint can't point inside a leaf node
				const nextNode = $contextHint.nodeAfter;
				if (!nextNode) return;

				// Get the position after said node
				const endOfRange = $contextHint.pos + nextNode.nodeSize; // .nodeSize should always be 1

				view.dispatch(view.state.tr.setSelection(NodeSelection.create(tr.doc, $contextHint.pos)));
				range = new NodeRange($contextHint, tr.doc.resolve(endOfRange), 0);
			} else {
				view.dispatch(
					view.state.tr.setSelection(TextSelection.create(tr.doc, $contextHint.start(), $contextHint.end())),
				);
				range = view.state.selection.$from.blockRange(view.state.selection.$to);
			}

			if (!range) {
				this.contextHint?.discard();
				this.contextHint = null;
				return;
			}
		}
	};

	moveSelect = (direction: "UP" | "DOWN") => {
		const view = this.editorView;
		if (!view) return false;

		const {
			selection: { $anchor, $head },
			doc,
			tr,
		} = view.state;

		// If either end is not at depth zero, this is a text selection (within a block).
		if ($anchor.depth != 0 || $head.depth != 0) {
			return false;
		}

		// Prevent this selection change from being adjusted by appendTransaction.
		tr.setMeta("multiselect-adjusted", true);

		// Adjust the head position according to the direction.
		let headPos: number = $head.pos;

		if (direction == "UP" && $head.nodeBefore) {
			headPos -= $head.nodeBefore.nodeSize;
		} else if (direction == "DOWN" && $head.nodeAfter) {
			headPos += $head.nodeAfter.nodeSize;
		}

		// Set the new selection as a BlockSelection.
		view.dispatch(tr.setSelection(new BlockSelection($anchor, doc.resolve(headPos))));

		return true;
	};

	deleteSelectedBlocks = () => {
		const view = this.editorView;
		if (!view) return false;
		const { state, dispatch } = view;
		const {
			selection: { $from, $to, empty },
		} = state;
		if (empty || $from.start(1) === $to.start(1)) return false;
		const [start, end] = expandSelectionToBlocks($from.pos, $to.pos, state.doc);
		const tr = state.tr;
		tr.deleteRange(start, end);
		if (dispatch) {
			dispatch(tr);
		}
		return true;
	};

	keys() {
		return {
			"Shift-ArrowUp": () => {
				return this.moveSelect("UP");
			},
			"Shift-ArrowDown": () => {
				return this.moveSelect("DOWN");
			},
			Backspace: this.deleteSelectedBlocks,
			"Mod-Backspace": this.deleteSelectedBlocks,
			Delete: this.deleteSelectedBlocks, // the normal delete on mac will trigger Mod-Delete
			"Mod-Delete": this.deleteSelectedBlocks,
		};
	}

	appendTransaction(transactions: readonly Transaction[], oldState: EditorState, newState: EditorState) {
		const { selection } = newState;

		// If the selection hasn't changed between transactions, do nothing.
		if (!selection || oldState.selection.eq(selection)) return;

		const { anchor, head, $from, $to } = selection;
		// If both ends of the selection point into the same parent node, do
		// nothing.
		if ($from.start() === $to.start()) return;

		const tr = newState.tr;
		tr.setMeta("multiselect-adjusted", true);

		// By default, right clicking outside a TextNode on the right side
		// selects just the pseudo-newline. Because this counts as a selection
		// across two lines, we end up with a selection across both lines.
		// This is default contentEditable behaviour. This change makes it
		// behave like google docs, where the cursor simply ends up at the end
		// of the line instead.
		let mouseSelect = false;
		for (const oldTr of transactions) {
			if (oldTr.getMeta("pointer")) {
				mouseSelect = true;
			}
			if (oldTr.getMeta("multiselect-adjusted")) return;
			if (
				oldTr.getMeta("pointer") === true &&
				$to.nodeBefore === null &&
				$from.nodeAfter === null &&
				mouseState !== "down"
			) {
				return tr.setSelection(new TextSelection($from));
			}
		}

		let startPos = $from.before();
		let endPos = $to.after();

		if (isCollapsed(newState, $to.node(1))) {
			[startPos, endPos] = expandRangeToIncludeDescendants(newState, $from, $to);
		}
		let newSelection = BlockSelection.create(tr.doc, startPos, endPos);
		if (anchor > head) {
			newSelection = BlockSelection.create(tr.doc, endPos, startPos);
		}
		if (mouseSelect) {
			tr.setMeta("pointer", true);
		}
		return tr.setSelection(newSelection);
	}
}

const dropIndicatorNotValid = document.createElement("div");
{
	dropIndicatorNotValid.classList.add("chevron");
	dropIndicatorNotValid.appendChild(
		new DOMParser()
			.parseFromString(
				// eslint-disable-next-line
				`<svg id="svg" aria-hidden="true" focusable="false" data-prefix="fal" data-icon="ban" class="svg-inline--fa fa-ban fa-w-16 " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zM103.265 408.735c-80.622-80.622-84.149-208.957-10.9-293.743l304.644 304.643c-84.804 73.264-213.138 69.706-293.744-10.9zm316.37-11.727L114.992 92.365c84.804-73.263 213.137-69.705 293.743 10.9 80.622 80.621 84.149 208.957 10.9 293.743z"></path></svg>`,
				"text/html",
			)
			// not nullable
			.getElementById("svg") as HTMLElement,
	);
}

const dropIndicatorValid = document.createElement("div");
{
	dropIndicatorValid.classList.add("chevron");
	const circle = document.createElement("div");
	circle.classList.add("multiselect-drop-indicator");
	const circleInner = document.createElement("div");
	circle.appendChild(circleInner);
	dropIndicatorValid.appendChild(circle);
}

const COLLAPSED_SECTION_EXPAND_DELAY = 2000;

export class MultiSelectDropHandler extends DropTargetHandler {
	get type() {
		return "json/fellow/prose";
	}

	private draggingOver?: { node: ProseNode; dom: HTMLElement; since: number };

	isCollapsedHeading(state: EditorState, node: ProseNode): boolean {
		return node.type.name === "header-one" && isCollapsed(state, node);
	}

	_onDragOver = (view: EditorView, event: Event, dropTarget: DropTarget) => {
		const { dom, node } = dropTarget.referenceDesc ?? {};

		if (this.draggingOver) {
			if (node === this.draggingOver.node) {
				if (
					Date.now() - this.draggingOver.since > COLLAPSED_SECTION_EXPAND_DELAY &&
					this.isCollapsedHeading(view.state, node)
				) {
					setCollapsed(view, node, false);
					this.draggingOver.dom.classList.remove("multiselect-section-will-expand");
				}
				return;
			} else {
				this.draggingOver.dom.classList.remove("multiselect-section-will-expand");
				delete this.draggingOver;
			}
		}

		if (node && this.isCollapsedHeading(view.state, node) && dom instanceof HTMLElement) {
			dom.classList.add("multiselect-section-will-expand");
			this.draggingOver = {
				node,
				dom,
				since: Date.now(),
			};
		}
	};

	onDragOver = throttle(this._onDragOver, 16);

	onDragLeave = (_view: EditorView, _event: Event, _dropTarget: DropTarget) => {
		if (this.draggingOver) {
			this.draggingOver.dom.classList.remove("multiselect-section-will-expand");
		}
	};

	onDrop(event: DragEvent, dropPos: number, view: EditorView, dropTarget: DropTarget) {
		this.draggingOver?.dom.classList.remove("multiselect-section-will-expand");
		delete this.draggingOver;
		// console.trace('multiselect drop', dropPos, dropTarget)
		if (!dropTarget.dropPosValid) return true;
		if (!event.dataTransfer) return true;
		const dataTransfer = event.dataTransfer;
		let slice = Slice.empty;
		let sideEffect = null;
		const draggedItem = view.dragging;
		view.dragging = null;

		if (draggedItem) {
			// internal dragging
			slice = draggedItem.slice;
		} else {
			// external dragging (another prosemirror document)
			const data = JSON.parse(dataTransfer.getData(this.type));
			slice = Slice.fromJSON(view.state.schema, data.slice);
			// external dragging. Do some sanitization.
			slice.content.forEach(node => {
				// We create an assignee set so it is faster to search assignees within it
				const assigneeSet = new Set(
					node.attrs.assignees?.map((assignee: { [key: string]: string }) => assignee.id) ?? [],
				);
				// if the current note has the assignee user too, don't strip the assignee
				(node.attrs as MutableProseNodeAttrs).assignees = getContext(view.state)?.participants?.find(
					(item: { id: string }) => assigneeSet.has(item.id),
				)
					? node.attrs.assignees
					: [];
				(node.attrs as MutableProseNodeAttrs).creator = null;
				(node.attrs as MutableProseNodeAttrs).moved = false;
			});
			sideEffect = () => {
				// This only applies to content moved/copied from another note.
				// If the user is holding shift, don't delete the original.
				// In other words, if the user is holding shift, this is a copy, not a move.
				if (event.shiftKey) return;

				const data = JSON.parse(dataTransfer.getData(this.type));
				const originalMultiselectUUID = data.originalMultiselectUUID as string;
				const originalSlice = data.slice as Slice;
				try {
					const originalMultiSelect = MultiselectMap.get(originalMultiselectUUID);
					if (originalMultiSelect && originalMultiSelect.editorView) {
						const originalNoteView = originalMultiSelect.editorView;
						const tr = originalNoteView.state.tr;
						originalSlice.content.forEach(n => {
							removeBlockById(n.attrs.blockId, tr);
						});
						originalNoteView.dispatch(tr);
					}
				} catch (error) {
					console.error(error);
				}
			};
		}
		const { doc } = view.state;
		const tr = view.state.tr;
		if (!draggedItem) {
			// for external drag/drop, don't let user undo this action.
			// Because we can't currently undo the corresponding deletion on the other note.
			tr.setMeta("addToHistory", false);
		}
		if (draggedItem && draggedItem.move) {
			const { from, to } = view.state.selection;
			const [start, end] = expandSelectionToBlocks(from, to, view.state.doc);
			tr.replaceRange(start, end, Slice.empty);
		}
		const mappedDropPos = tr.mapping.map(dropPos);

		tr.insert(mappedDropPos, slice.content);

		// adjust indentation level
		// doing this after inserting will introduce new steps but will make undo a lot better.
		let referenceNode: ProseNode | null | undefined = null;

		if (dropTarget.referenceDesc) {
			referenceNode = dropTarget.referenceDesc.node;
		}

		if (!referenceNode) {
			referenceNode = doc.resolve(dropPos).nodeBefore;
		}

		const firstNode = slice.content.child(0);
		if (referenceNode) {
			let referenceIndent = referenceNode.attrs.indent || 0;
			if (referenceIndent === 0 && LIST_ITEM_TYPE_NAMES.includes(firstNode.type.name)) {
				// List items should have at least one level of indentation
				referenceIndent = 1;
			}
			const sliceBlockTree = buildTree(slice);
			for (const child of sliceBlockTree.root.children) {
				if (!child.block) continue;
				if (child.block.type.name === "header-one") continue;
				for (const treeNode of [child, ...child.descendants()]) {
					if (!treeNode.block) continue;

					const relativeIndent = treeNode.block.attrs.indent - (firstNode.attrs.indent || 0);
					let indent = relativeIndent + referenceIndent;

					if (indent > 6) indent = 6;
					if (indent < 0) indent = 0;
					if (indent !== treeNode.block.attrs.indent) {
						tr.setNodeMarkup(mappedDropPos + treeNode.start, undefined, { ...treeNode.block.attrs, indent });
					}
				}
			}
		}

		// select whatever is inserted
		tr.setMeta("multiselect-adjusted", true);

		const node = tr.doc.nodeAt(dropPos);
		let selection, shouldFocus;
		if (node && (!!node.attrs.originalBlockId || node.isLeaf) && NodeSelection.isSelectable(node)) {
			shouldFocus = false;
			selection = NodeSelection.create(tr.doc, tr.mapping.map(dropPos, -1));
		} else {
			shouldFocus = true;
			selection = BlockSelection.create(tr.doc, tr.mapping.map(dropPos, -1), tr.mapping.map(dropPos, -1) + slice.size);
		}
		tr.setSelection(selection);

		if (!tr.doc.eq(tr.docs[0])) {
			view.dispatch(tr);
		}

		if (shouldFocus) {
			window.setTimeout(() => view.focus(), 0);
		}

		view.dragging = null;
		view.dispatch(view.state.tr.setMeta(multiSelectPluginKey, { dragging: false }));
		if (sideEffect) sideEffect();
		return true;
	}

	updateIndicator = (indicator: HTMLElement, _doc: ProseNode, view: EditorView, dropTarget: DropTarget): void => {
		let el;
		if (dropTarget.dropPosValid && !indicator.contains(dropIndicatorValid)) {
			el = dropIndicatorValid;
		} else if (!dropTarget.dropPosValid && !indicator.contains(dropIndicatorNotValid)) {
			el = dropIndicatorNotValid;
		} else {
			return;
		}

		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (imageStripExt) {
			if (dropTarget.referenceDesc?.node?.type.name === "image_strip_item") {
				indicator.classList.add("over-image-strip");
			} else {
				indicator.classList.remove("over-image-strip");
			}
		}

		if (indicator.firstChild) {
			indicator.replaceChild(el, indicator.firstChild);
		} else {
			indicator.appendChild(el);
		}
	};

	validateDropPos(pos: number, doc: ProseNode, view: EditorView, desc: ViewDesc) {
		const skip = false;
		const targetIsImageStrip = desc.node && desc.node.type.name === "image_strip_item";

		if (targetIsImageStrip || posIsInCollapsedSection(view.state, pos)) {
			return {
				valid: false,
				skip: true,
			};
		}

		// don't allow drop inside dragged blocks
		if (view.dragging) {
			const { from, to } = view.state.selection;
			const [start, end] = expandSelectionToBlocks(from, to, doc);
			if (pos > start && pos <= end) return { valid: false, skip };
		}
		return { valid: true, skip };
	}
}
